import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  fetchPharmacies, 
  createPharmacy, 
  updatePharmacy, 
  deletePharmacy,
  fetchStates,
  api
} from '../services/api';

// Styled Components
const Container = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--border-color);
`;

const Title = styled.h1`
  color: var(--text-color);
  margin: 0;
  font-size: 28px;
`;

const Button = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Card = styled.div`
  background-color: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  input {
    flex: 1;
    padding: 12px 15px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 16px;
    background-color: var(--input-bg);
    color: var(--text-color);
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.2);
    }
  }
`;

const AddButton = styled(Button)`
  margin-left: 15px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: var(--text-secondary);
`;

const StateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  background-color: rgba(var(--success-light-rgb), 0.3);
  color: var(--success-color);
  border: 1px solid rgba(var(--success-color-rgb), 0.3);
  font-size: 0.8rem;
  margin-right: 8px;
  margin-bottom: 8px;
  font-weight: 600;
  
  &:hover {
    background-color: var(--success-light-hover);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$delete ? 'var(--error-color)' : 'var(--primary-color)'};
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$delete ? 'rgba(var(--error-light-rgb), 0.2)' : 'rgba(var(--primary-light-rgb), 0.2)'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--card-bg);
  padding: 30px;
  border-radius: 8px;
  width: 700px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: var(--text-color);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: var(--text-color);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: var(--text-color);
  font-weight: 500;
  font-size: 15px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--input-bg);
  color: var(--text-color);
  font-size: 15px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(67, 80, 141, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background-color: var(--input-bg);
  color: var(--text-color);
  font-size: 15px;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(67, 80, 141, 0.2);
  }
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
`;

const StateTag = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 20px;
  background-color: ${props => props.selected ? 'var(--success-color)' : 'var(--bg-secondary)'};
  color: ${props => props.selected ? 'white' : 'var(--text-color)'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.selected ? 'var(--success-dark)' : 'var(--hover-bg)'};
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 14px;
  margin-top: 5px;
`;

const ConfirmationModal = styled(Modal)``;

const ConfirmationContent = styled(ModalContent)`
  max-width: 500px;
`;

const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled(Button)`
  background-color: var(--secondary-color);
  
  &:hover {
    background-color: var(--secondary-dark);
  }
`;

const DeleteButton = styled(Button)`
  background-color: var(--error-color);
  
  &:hover {
    background-color: var(--error-dark);
  }
`;

const PharmacyManagementPage = () => {
  const [pharmacies, setPharmacies] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    states: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pharmacyToDelete, setPharmacyToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch pharmacies and states on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Fetch pharmacies and states
  const fetchData = async () => {
    try {
      setLoading(true);
      const [pharmaciesData, statesData] = await Promise.all([
        fetchPharmacies(),
        fetchStates()
      ]);
      setPharmacies(pharmaciesData);
      setStates(statesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the modal for adding a new pharmacy
  const handleAddPharmacy = () => {
    setModalMode('add');
    setFormData({
      name: '',
      address: '',
      states: []
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Handle opening the modal for editing an existing pharmacy
  const handleEditPharmacy = (pharmacy) => {
    setModalMode('edit');
    setFormData({
      id: pharmacy.pharmacy_id,
      name: pharmacy.pharmacy_name,
      address: pharmacy.pharmacy_address || '',
      states: pharmacy.states.map(state => state.toString())
    });
    setFormErrors({});
    setShowModal(true);
  };

  // Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle state tag changes
  const handleStateTagChange = (stateId) => {
    setFormData(prev => {
      if (prev.states.includes(stateId)) {
        return {
          ...prev,
          states: prev.states.filter(id => id !== stateId)
        };
      } else {
        return {
          ...prev,
          states: [...prev.states, stateId]
        };
      }
    });
  };

  // Validate form data
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Pharmacy name is required';
    }
    
    if (!formData.states.length) {
      errors.states = 'At least one state must be selected';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const pharmacyData = {
        pharmacy_name: formData.name,
        pharmacy_address: formData.address,
        states: formData.states.map(id => parseInt(id))
      };
      
      if (modalMode === 'add') {
        // Create new pharmacy
        await createPharmacy(pharmacyData);
      } else {
        // Update existing pharmacy
        await updatePharmacy(formData.id, pharmacyData);
      }
      
      // Refresh pharmacies list
      await fetchData();
      
      // Close modal
      setShowModal(false);
      
    } catch (error) {
      console.error('Error saving pharmacy:', error);
      
      // Handle validation errors from backend
      if (error.response && error.response.data) {
        const backendErrors = {};
        
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value;
        });
        
        setFormErrors(backendErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle opening the confirmation modal for deleting a pharmacy
  const handleDeleteConfirmation = (pharmacy) => {
    setPharmacyToDelete(pharmacy);
    setShowConfirmation(true);
  };

  // Handle deleting a pharmacy
  const handleDeletePharmacy = async () => {
    if (!pharmacyToDelete) return;
    
    try {
      setLoading(true);
      
      // Delete pharmacy
      await deletePharmacy(pharmacyToDelete.pharmacy_id);
      
      // Refresh pharmacies list
      await fetchData();
      
      // Close confirmation modal
      setShowConfirmation(false);
      setPharmacyToDelete(null);
      
    } catch (error) {
      console.error('Error deleting pharmacy:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter pharmacies based on search term
  const filteredPharmacies = pharmacies.filter(pharmacy => 
    pharmacy.pharmacy_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pharmacy.pharmacy_address && pharmacy.pharmacy_address.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Render state badges
  const renderStateBadges = (stateIds) => {
    if (!stateIds || !stateIds.length) return <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No states assigned</span>;
    
    return stateIds.map((stateId, index) => {
      const state = states.find(s => s.state_id === parseInt(stateId));
      if (!state) return null;
      
      return (
        <StateBadge key={`state-${stateId}-${index}`}>
          {state.state_name}
        </StateBadge>
      );
    });
  };

  return (
    <Container>
      <Header>
        <Title>Pharmacy Management</Title>
      </Header>
      
      <Card>
        <SearchBar>
          <input 
            type="text" 
            placeholder="Search pharmacies..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <AddButton onClick={handleAddPharmacy}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6V12M12 12V18M12 12H18M12 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Add Pharmacy
          </AddButton>
        </SearchBar>
      </Card>
      
      {loading && pharmacies.length === 0 ? (
        <LoadingContainer>Loading pharmacies...</LoadingContainer>
      ) : filteredPharmacies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
          No pharmacies found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredPharmacies.map(pharmacy => (
            <Card key={pharmacy.pharmacy_id} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.125rem' }}>{pharmacy.pharmacy_name}</h3>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    {pharmacy.pharmacy_address || 'No address provided'}
                  </div>
                </div>
                
                <ActionButtons>
                  <ActionButton onClick={() => handleEditPharmacy(pharmacy)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.44772 4 3 4.44772 3 5V19C3 19.5523 3.44772 20 4 20H18C18.5523 20 19 19.5523 19 19V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Edit
                  </ActionButton>
                  <ActionButton $delete onClick={() => handleDeleteConfirmation(pharmacy)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Delete
                  </ActionButton>
                </ActionButtons>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>Licensed States</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {renderStateBadges(pharmacy.states)}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Add/Edit Pharmacy Modal */}
      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {modalMode === 'add' ? 'Add New Pharmacy' : 'Edit Pharmacy'}
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Pharmacy Name*</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter pharmacy name"
                />
                {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label>Address</Label>
                <Input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter address (optional)"
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Licensed States*</Label>
                <TagContainer>
                  {states && states.map(state => (
                    <StateTag 
                      key={state?.state_id || Math.random()} 
                      selected={formData.states.includes((state?.state_id || '').toString())} 
                      onClick={() => handleStateTagChange((state?.state_id || '').toString())}
                    >
                      {state?.state_name || 'Unknown State'}
                    </StateTag>
                  ))}
                </TagContainer>
                {formErrors.states && <ErrorMessage>{formErrors.states}</ErrorMessage>}
              </FormGroup>
              
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : modalMode === 'add' ? 'Add Pharmacy' : 'Update Pharmacy'}
              </Button>
            </Form>
          </ModalContent>
        </Modal>
      )}
      
      {/* Delete Confirmation Modal */}
      {showConfirmation && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ModalHeader>
              <ModalTitle>Confirm Delete</ModalTitle>
              <CloseButton onClick={() => setShowConfirmation(false)}>&times;</CloseButton>
            </ModalHeader>
            
            <p>Are you sure you want to delete the pharmacy "{pharmacyToDelete?.pharmacy_name}"?</p>
            <p>This action cannot be undone.</p>
            
            <ConfirmationActions>
              <CancelButton onClick={() => setShowConfirmation(false)}>
                Cancel
              </CancelButton>
              <DeleteButton onClick={handleDeletePharmacy} disabled={loading}>
                {loading ? 'Deleting...' : 'Delete'}
              </DeleteButton>
            </ConfirmationActions>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </Container>
  );
};

export default PharmacyManagementPage;
